import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { MaterialsService } from '../../../../services/materials.service';
import { MaterialListRowDto } from '@models/material-list-row-dto.interface';
import { SearchParameters } from '@shared/models/search-parameters.model';
import { MaterialListQuery } from '@models/material-list-query.interface';
import { MaterialDetailDialogComponent } from '../material-detail-dialog/material-detail-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { EditMaterialDialogComponent } from '../edit-material-dialog/edit-material-dialog.component';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-materials-table',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule, MatTableModule, MatPaginatorModule, MatIconModule, MatButtonModule],
  templateUrl: './materials-table.component.html',
  styleUrl: './materials-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialsTableComponent implements OnInit {
  private readonly materialsService = inject(MaterialsService);
  private readonly dialog = inject(MatDialog);

  protected searchControl = new FormControl('');
  protected isLoading = signal(false);
  protected searchDone = signal(false);
  protected dataSource = new MatTableDataSource<MaterialListRowDto>([]);
  protected displayedColumns: string[] = ['number', 'edit', 'delete', 'request', 'requestDate', 'well', 'rig', 'totalAmount'];
  protected totalCount = 0;
  protected pageIndex = 0;
  protected pageSize = 20;
  protected selectedRowId = signal<number | null>(null);

  ngOnInit(): void {
    this.load(0);

    this.searchControl.valueChanges
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe(() => {
        this.pageIndex = 0;
        this.load(0);
      });
  }

  protected onDeleteClick(row: MaterialListRowDto, event: MouseEvent): void {
    event.stopPropagation();
    this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Sample',
        primaryText: `Are you sure you want to delete ${row.title ?? 'this sample'}?`,
        confirmText: 'Delete',
      },
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.materialsService.deleteMaterial(row.id).subscribe({
          next: () => this.reload(),
        });
      }
    });
  }

  protected onEditClick(row: MaterialListRowDto, event: MouseEvent): void {
    event.stopPropagation();
    this.dialog.open(EditMaterialDialogComponent, {
      width: '750px',
      minWidth: '750px',
      maxHeight: '90vh',
      data: { materialId: row.id },
    }).afterClosed().subscribe((updatedId: number | undefined) => {
      if (updatedId) {
        this.reload();
      }
    });
  }

  protected onRowClick(row: MaterialListRowDto): void {
    this.selectedRowId.set(row.id);
    this.dialog.open(MaterialDetailDialogComponent, {
      width: '600px',
      minWidth: '600px',
      maxHeight: '90vh',
      data: { materialId: row.id },
    }).afterClosed().subscribe(() => {
      this.selectedRowId.set(null);
    });
  }

  protected onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.load(this.pageIndex);
  }

  private load(pageIndex: number): void {
    this.isLoading.set(true);
    this.searchDone.set(false);
    const params: SearchParameters<MaterialListQuery> = {
      query: { filter: this.searchControl.value?.trim() || null },
      pageIndex,
      pageSize: this.pageSize,
    };

    this.materialsService.searchMaterials(params).subscribe({
      next: (result) => {
        this.dataSource.data = result.items;
        this.totalCount = result.totalCount;
        this.pageIndex = result.pageIndex;
        this.pageSize = result.pageSize;
        this.isLoading.set(false);
        this.searchDone.set(true);
      },
      error: () => {
        this.dataSource.data = [];
        this.totalCount = 0;
        this.isLoading.set(false);
        this.searchDone.set(true);
      },
    });
  }

  reload(): void {
    this.pageIndex = 0;
    this.load(0);
  }
}
